/**
 * KYC tier limits — the ceilings every outflow is measured against.
 *
 * Each row is one account tier and the three numbers `assertWithinLimits` enforces on it:
 * the most that may leave the wallet in a single transaction, in a Lagos day, and in a Lagos
 * month. They are not a display of policy — they *are* the policy. The limits service reads
 * this table fresh on every check, so a number saved here is in force on the next
 * transaction any customer at that tier attempts. The page says so plainly, at the top,
 * because "when does this take effect" is the first question anyone asks of it and getting
 * the answer wrong in the reassuring direction is how a ceiling gets moved on a whim.
 *
 * Three things the design leans on:
 *
 * - **Naira in, kobo out, `BigInt` throughout.** The operator types ₦500,000.00 and the API
 *   is sent `50000000`. Nothing here calls `Number` or `parseFloat` on an amount:
 *   `Math.round(value * 100)` is off by a kobo on figures this size, and a ceiling that is
 *   one kobo low refuses a transaction the console says is allowed.
 * - **`single ≤ daily ≤ monthly` is checked before saving.** The API enforces it, but the
 *   failure it prevents is silent rather than loud — a daily ceiling below the single one
 *   errors nowhere, it just makes the single ceiling unreachable — so the operator is told
 *   next to the fields rather than after a round-trip.
 * - **tier_0 is zeros, and that is the point.** A registered-but-unverified account cannot
 *   move money at all, and these three zeros are how that is enforced. The row is editable
 *   like any other, with a warning attached, rather than hidden.
 *
 * Gated on `kyc:review` — compliance and superadmin — on the read as well as the write. A
 * tier's ceilings *are* the tier: the role that decides a customer reaches tier_2 is the role
 * that should own what tier_2 lets them move. Support answering a ticket already sees an
 * individual customer's limits on the user detail, which is what a ticket needs.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { SetTierLimitsRequest, TierLimit } from '../contracts/types.js';
import { Badge, Button, Card, ErrorBox, Input, LoadingState } from '../components/ui.js';
import { TierBadge } from '../components/badges.js';
import { IconAlert } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { relativeTime } from '../lib/format.js';
import { formatMoney, minorUnitsToInput, parseNairaToKobo } from '../lib/money.js';

/**
 * What each tier is, in the words an operator would use.
 *
 * Kept beside the numbers because a ceiling with no idea of who it applies to is a number
 * without a meaning — and because the evidence a tier asks for is what justifies the size of
 * its ceiling.
 */
const TIER_COPY: Readonly<Record<string, string>> = {
  tier_0: 'Registered, nothing verified. Cannot move money at all, and these zeros are what enforces that.',
  tier_1: 'A phone number verified by one-time code. The entry tier.',
  tier_2: 'A BVN or NIN matching the name and date of birth. The design’s default account.',
  tier_3: 'Everything tier 2 asks for, plus a government photo ID and a checked home address.',
};

type Ceiling = 'single' | 'daily' | 'monthly';

const CEILINGS: readonly Ceiling[] = ['single', 'daily', 'monthly'];

const CEILING_LABELS: Readonly<Record<Ceiling, string>> = {
  single: 'Per transaction',
  daily: 'Per day',
  monthly: 'Per month',
};

/**
 * What each ceiling actually stops, in the operator's terms.
 *
 * The labels alone leave the two questions an operator actually has unanswered: *which*
 * movements count, and what happens when one is exceeded. Both matter — a ceiling that
 * quietly excluded bill payments would be set far too low by someone assuming it covered
 * everything, and "does it queue or refuse" is the difference between a support ticket and a
 * customer who thinks the app is broken.
 */
const CEILING_HELP: Readonly<Record<Ceiling, string>> = {
  single: 'The largest single movement out. A transfer, withdrawal, bill or conversion above this is refused outright. Nothing is queued and nothing partially sends.',
  /*
   * These two said "a rolling 24 hours" and "a rolling 30 days" and the server has never
   * worked that way: it sums from the start of the current calendar day and the start of the
   * current calendar month, both in Nigerian time. The difference is the whole answer to the
   * question support is actually asked — "when can I send again?" — and the old wording sent
   * an agent to tell a customer to wait a day when the ceiling in fact clears at midnight.
   */
  daily: 'Total money out since midnight, Nigerian time. It resets at midnight, not 24 hours after the last transfer. Counted in naira, so a coin movement is valued at the rate when it happened.',
  monthly: 'Total money out since the 1st of the month, Nigerian time. It resets on the 1st. Must be at or above the daily ceiling, or the daily one can never be reached.',
};

/** The three inputs for one tier, as naira strings the operator is editing. */
type Draft = Readonly<Record<Ceiling, string>>;

function draftOf(row: TierLimit): Draft {
  return {
    single: minorUnitsToInput(row.single.amount, 2),
    daily: minorUnitsToInput(row.daily.amount, 2),
    monthly: minorUnitsToInput(row.monthly.amount, 2),
  };
}

export function KycLimitsPage(): JSX.Element {
  const { api, session } = useAuth();
  const canWrite = session !== null && adminCan(session.me.role, 'kyc:review');
  const state = useAsync(() => api.listTierLimits(), []);

  if (state.loading) return <LoadingState label="Loading tier ceilings…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (state.data === null) return <ErrorBox message="No tier ceilings are available." />;

  const tiers = state.data.tiers;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Spending limits</h1>
        <p className="page-head__sub">
          How much a customer can move out at each tier: transfers, bills, crypto sends, all of
          it, in naira. A change applies to everyone on that tier straight away, and never to
          money that has already moved. Your name is recorded on each change.
          {!canWrite && ' Your role can view these but not change them.'}
        </p>
        <p className="page-head__sub">
          Tier&nbsp;0 is zeros, so an unverified account can move nothing. To stop everyone below
          Tier&nbsp;2 regardless of amount, turn on <code>require_kyc</code> on the{' '}
          <Link to="/flags">feature flags</Link> page. That is a separate rule and changes no
          number here.
        </p>
      </div>

      <div className="limit-grid">
        {tiers.map((row) => (
          <TierCard
            // Remounts once the server confirms a save, so the inputs reset to what is now
            // stored — and only then, so an edit in progress on another tier survives it.
            key={`${row.tier}:${row.updatedAt ?? 'never'}`}
            row={row}
            canWrite={canWrite}
            onSaved={(updated) =>
              state.setData((prev) =>
                prev === null
                  ? prev
                  : { tiers: prev.tiers.map((t) => (t.tier === updated.tier ? updated : t)) },
              )
            }
          />
        ))}
      </div>
    </>
  );
}

function TierCard({
  row,
  canWrite,
  onSaved,
}: {
  row: TierLimit;
  canWrite: boolean;
  onSaved: (updated: TierLimit) => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft>(() => draftOf(row));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stored = draftOf(row);
  const dirty = CEILINGS.some((c) => draft[c] !== stored[c]);

  // Parsed once per render, in `BigInt`, and reused for both the ordering check and the
  // body. A ceiling that will not parse is null, which reads as "not a figure yet" rather
  // than as zero — zero is a real and meaningful ceiling here, so the two must not collapse.
  const kobo: Readonly<Record<Ceiling, bigint | null>> = {
    single: parseNairaToKobo(draft.single),
    daily: parseNairaToKobo(draft.daily),
    monthly: parseNairaToKobo(draft.monthly),
  };

  const unparsed = CEILINGS.filter((c) => kobo[c] === null);
  const orderProblem =
    unparsed.length > 0
      ? null
      : kobo.single! > kobo.daily!
        ? 'The per-transaction ceiling is above the daily one. That does not error anywhere. It just makes the per-transaction figure unreachable, so customers would be refused on an amount this page says is allowed.'
        : kobo.daily! > kobo.monthly!
          ? 'The daily ceiling is above the monthly one, which makes the daily figure unreachable for most of the month.'
          : null;

  const blocked = unparsed.length > 0 || orderProblem !== null;

  async function save(): Promise<void> {
    if (blocked) return;
    setError(null);
    setSaving(true);
    try {
      const body: SetTierLimitsRequest = {
        single: { currency: 'NGN', amount: kobo.single!.toString() },
        daily: { currency: 'NGN', amount: kobo.daily!.toString() },
        monthly: { currency: 'NGN', amount: kobo.monthly!.toString() },
      };
      const updated = await api.setTierLimits(row.tier, body);
      onSaved(updated);
      toast.success(
        `${row.tier.replace('tier_', 'Tier ')} ceilings saved`,
        'In force now. The next limit check on any customer at this tier uses them.',
      );
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <TierBadge tier={row.tier} />
          {row.tier === 'tier_0' && <Badge tone="neutral">Cannot transact</Badge>}
        </span>
      }
      actions={
        <span className="faint" style={{ fontSize: 12 }}>
          {row.updatedAt === null
            ? 'never changed from the seeded policy'
            : `changed ${relativeTime(row.updatedAt)}`}
        </span>
      }
    >
      <p className="muted" style={{ marginTop: -4 }}>
        {TIER_COPY[row.tier] ?? 'An account tier.'}
      </p>

      <div className="limit-fields">
        {CEILINGS.map((ceiling) => {
          const id = `limit-${row.tier}-${ceiling}`;
          const parsed = kobo[ceiling];
          return (
            <div className="limit-field" key={ceiling}>
              <label className="field__label" htmlFor={id}>
                {CEILING_LABELS[ceiling]}
              </label>
              <p className="muted limit-field__help">{CEILING_HELP[ceiling]}</p>
              <div className="limit-field__input">
                <span className="limit-field__symbol">₦</span>
                <Input
                  id={id}
                  className="mono"
                  inputMode="decimal"
                  value={draft[ceiling]}
                  disabled={!canWrite || saving}
                  onChange={(e) => setDraft((prev) => ({ ...prev, [ceiling]: e.target.value }))}
                />
              </div>
              <div className="limit-field__note">
                {parsed === null ? (
                  <span className="limit-field__bad">Not an amount.</span>
                ) : (
                  // The grouped rendering of exactly the kobo that will be sent, so the
                  // operator confirms the figure rather than the keystrokes.
                  <span className="faint">
                    {formatMoney({ currency: 'NGN', amount: parsed.toString() })}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {orderProblem !== null && (
        <div className="error-box" style={{ marginTop: 12 }} role="alert">
          <IconAlert size={16} />
          <div>{orderProblem}</div>
        </div>
      )}

      {error !== null && <ErrorBox message={error} />}

      <div className="limit-foot">
        {/* The contract gives an admin id and no name, so a short handle with the whole id
            on hover is the honest rendering: enough to correlate against the audit log,
            without a 36-character UUID sitting in the middle of a card. */}
        <span className="faint" style={{ fontSize: 12 }} title={row.updatedByAdminId ?? undefined}>
          {row.updatedByAdminId === null
            ? 'Still on the seeded policy'
            : `Last set by admin …${row.updatedByAdminId.slice(-6)}`}
        </span>
        <Button
          variant="primary"
          loading={saving}
          disabled={!canWrite || !dirty || blocked}
          onClick={() => void save()}
        >
          Save ceilings
        </Button>
      </div>
    </Card>
  );
}
